<template>
  <!-- One dialog, two panes. Line items are reference data for this form, so they open as its
       right pane rather than as a second window: one surface, one shadow, tops aligned by
       construction, and both halves live without any scrim or focus-trap juggling. -->
  <v-dialog v-model="dialog.open.value" :max-width="dialogWidth" persistent>
    <div class="flex overflow-hidden rounded-xl bg-white" :class="paneOpen ? 'h-[88vh]' : 'max-h-[88vh]'">
      <div class="schedule-dialog flex flex-col min-w-0 shrink-0 overflow-y-auto p-5" :style="{ width: `${FORM_WIDTH}px` }">
        <h3 class="m-0 text-base font-semibold text-gray-900">Schedule purchase order</h3>
        <p class="mb-0 mt-1 text-xs text-gray-500">Pick a PO — or leave it empty if you don't want to specify one, and say what the capacity is for.</p>

        <div class="mt-4 flex gap-3">
          <div class="flex-1">
            <label class="label" for="dialog-vendor">Vendor</label>
            <v-select
              id="dialog-vendor"
              ref="vendorField"
              variant="outlined"
              density="compact"
              hide-details
              :items="dialog.vendorOptions.value"
              item-title="vendorCode"
              item-value="vendorId"
              :model-value="dialog.vendorId.value"
              :disabled="dialog.splitOpen.value && dialog.split.locked.value"
              :error="!!dialog.errorFor('vendor')"
              :aria-invalid="!!dialog.errorFor('vendor')"
              :aria-describedby="dialog.errorFor('vendor') ? 'dialog-vendor-error' : undefined"
              @update:model-value="onVendorChange"
            />
            <p v-if="dialog.errorFor('vendor')" id="dialog-vendor-error" class="mb-0 mt-1 text-xs text-red-600">{{ dialog.errorFor('vendor') }}</p>
          </div>
          <div class="flex-[1.4]">
            <label class="label" for="dialog-week">{{ dialog.splitOpen.value ? 'First week' : 'Week' }}</label>
            <v-select
              id="dialog-week"
              ref="weekField"
              variant="outlined"
              density="compact"
              hide-details
              :items="dialog.weekBuckets"
              item-title="label"
              item-value="key"
              :model-value="dialog.weekKey.value"
              :disabled="dialog.splitOpen.value && dialog.split.locked.value"
              :error="!!dialog.errorFor('week')"
              :aria-invalid="!!dialog.errorFor('week')"
              :aria-describedby="dialog.errorFor('week') ? 'dialog-week-error' : undefined"
              @update:model-value="onWeekChange"
            />
            <p v-if="dialog.errorFor('week')" id="dialog-week-error" class="mb-0 mt-1 text-xs text-red-600">{{ dialog.errorFor('week') }}</p>
          </div>
        </div>

        <div class="mt-3">
          <label class="label" for="dialog-po">Purchase Order</label>
          <v-autocomplete
            id="dialog-po"
            variant="outlined"
            density="compact"
            hide-details
            placeholder="Type to search PO numbers"
            persistent-placeholder
            :items="dialog.schedulablePos.value"
            :item-props="poItemProps"
            item-title="poNumber"
            item-value="poNumber"
            :model-value="dialog.poNumber.value"
            :loading="poLoading"
            :disabled="!dialog.vendorId.value || (dialog.splitOpen.value && dialog.split.locked.value)"
            clearable
            @update:model-value="dialog.selectPo"
            @update:search="dialog.searchPos"
          />
          <span v-if="poStore.error" class="text-xs text-red-600" role="alert">{{ poStore.error }}</span>
          <p class="mb-0 mt-1 text-xs text-gray-500">Leave empty if you don't want to specify a PO — a note is required instead.</p>
          <div class="mt-2 flex gap-2">
            <button
              v-if="dialog.selectedPo.value && dialog.selectedPo.value.lines.length > 0"
              class="pane-btn"
              :class="{ 'pane-btn--on': dialog.paneTab.value === 'lines' }"
              type="button"
              :aria-pressed="dialog.paneTab.value === 'lines'"
              :disabled="dialog.split.locked.value"
              @click="dialog.paneTab.value === 'lines' ? dialog.closePane() : dialog.openLineItems()"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M8 6h13M8 12h13M8 18h13" />
                <circle cx="3.5" cy="6" r="1" />
                <circle cx="3.5" cy="12" r="1" />
                <circle cx="3.5" cy="18" r="1" />
              </svg>
              Line items
            </button>
            <button
              v-if="dialog.canOpenSplit.value || dialog.splitOpen.value"
              class="pane-btn"
              :class="{ 'pane-btn--on': dialog.splitOpen.value }"
              type="button"
              :aria-pressed="dialog.splitOpen.value"
              :disabled="dialog.split.locked.value"
              @click="dialog.splitOpen.value ? dialog.closePane() : dialog.openSplit()"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="3" y="4" width="18" height="16" rx="2" />
                <path d="M3 10h18M9 10v10M15 10v10" />
              </svg>
              Split across weeks
            </button>
          </div>
          <p v-if="dialog.showUncountableStrip.value" class="mb-0 mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" role="note">
            {{ dialog.scheduledSummary.value ? `Scheduled so far: ${dialog.scheduledSummary.value}.` : 'Nothing scheduled yet.' }}
            Some lines can't be counted, so the portal can't tell when this PO is done.
          </p>
        </div>

        <div
          v-if="dialog.typeRows.value.length > 0"
          ref="quantitiesGroup"
          class="mt-4 space-y-2"
          role="group"
          aria-label="Quantities"
          :aria-describedby="dialog.errorFor('quantities') ? 'dialog-quantities-error' : undefined"
        >
          <div class="flex items-baseline justify-between">
            <span class="text-xs font-semibold text-gray-700">Quantities</span>
            <span v-if="dialog.splitOpen.value" class="text-[11px]" :class="splitHintClass">{{ splitHint }}</span>
          </div>

          <template v-if="dialog.splitOpen.value">
            <div
              class="px-3 pb-0.5 text-[9.5px] font-semibold uppercase tracking-wider text-gray-400"
              :class="showCalculated ? 'schedule-dialog__qty-grid--totals' : 'schedule-dialog__qty-grid--totals-plan-only'"
            >
              <span>Type</span>
              <span v-if="showCalculated" class="text-right">Calculated</span>
              <span class="text-right">{{ dialog.isManualMode.value ? 'Total to reserve' : 'PO total' }}</span>
            </div>
            <div
              v-for="row in dialog.typeRows.value"
              :key="row.capacityTypeId"
              class="rounded-lg border border-gray-100 bg-gray-50 px-3 py-1.5"
              :class="showCalculated ? 'schedule-dialog__qty-grid--totals' : 'schedule-dialog__qty-grid--totals-plan-only'"
            >
              <span class="text-sm font-semibold text-gray-900">{{ row.typeCode }}</span>
              <span v-if="showCalculated" class="rounded-md border border-dashed border-gray-200 bg-white px-2 py-1 text-right text-sm tabular-nums text-gray-500">
                {{ (dialog.calculatedByTypeId.value[row.capacityTypeId] ?? 0).toLocaleString() }}
              </span>
              <span class="text-right text-sm tabular-nums" :class="(dialog.frozenTotals.value[row.capacityTypeId] ?? 0) > 0 ? 'font-semibold text-gray-900' : 'text-gray-400'">
                {{ (dialog.frozenTotals.value[row.capacityTypeId] ?? 0) > 0 ? (dialog.frozenTotals.value[row.capacityTypeId] ?? 0).toLocaleString() : '—' }}
              </span>
            </div>
            <p class="mb-0 px-3 pt-0.5 text-[10px] text-gray-400">
              Weekly quantities are edited in the split. Close the split to change the {{ dialog.isManualMode.value ? 'totals' : 'PO totals or the adjustment' }}.
            </p>
          </template>
          <template v-else>
            <!-- The two capacity figures are named once here so the rows below can be bare
                 numbers: repeating "left" and "overbooked" on every row wrapped the line and
                 buried the digits. "Left" is the week as it stands, "After" is the week once
                 this plan is saved, and an `over` marker replaces the minus sign. -->
            <div
              class="px-3 pb-0.5 text-[9.5px] font-semibold uppercase tracking-wider text-gray-400"
              :class="showCalculated ? 'schedule-dialog__qty-grid' : 'schedule-dialog__qty-grid--plan-only'"
            >
              <span>Type</span>
              <span v-if="showCalculated" class="text-right">Calculated</span>
              <span class="text-center">Plan</span>
              <span class="text-right">Left</span>
              <span class="text-right">After</span>
            </div>

            <div
              v-for="row in dialog.typeRows.value"
              :key="row.capacityTypeId"
              class="rounded-lg border border-gray-100 bg-gray-50 px-3 py-1.5"
              :class="showCalculated ? 'schedule-dialog__qty-grid' : 'schedule-dialog__qty-grid--plan-only'"
            >
              <span class="text-sm font-semibold text-gray-900">{{ row.typeCode }}</span>
              <span
                v-if="showCalculated"
                class="rounded-md border border-dashed border-gray-200 bg-white px-2 py-1 text-right text-sm tabular-nums text-gray-500"
                :aria-label="`${row.typeLabel} calculated`"
              >
                {{ (dialog.calculatedByTypeId.value[row.capacityTypeId] ?? 0).toLocaleString() }}
              </span>
              <v-text-field
                class="schedule-dialog__quantity-field"
                variant="outlined"
                density="compact"
                type="number"
                min="0"
                hide-details
                :aria-label="`${row.typeLabel} plan`"
                :disabled="!row.hasPlan || row.alreadyScheduled"
                :model-value="dialog.quantities.value[row.capacityTypeId] ?? 0"
                @update:model-value="(value: string) => dialog.setQuantity(row.capacityTypeId, Number(value))"
              />
              <span v-if="!row.hasPlan" class="col-span-2 text-right text-xs text-gray-500">No plan this week</span>
              <span v-else-if="row.alreadyScheduled" class="col-span-2 text-right text-xs text-gray-500">Already scheduled</span>
              <template v-else>
                <span class="text-right text-xs tabular-nums" :class="remainingClass(row.remaining ?? 0)">
                  {{ magnitude(row.remaining ?? 0) }}<em v-if="(row.remaining ?? 0) < 0" class="schedule-dialog__over">over</em>
                </span>
                <span v-if="plannedQuantity(row) > 0" class="text-right text-xs tabular-nums" :class="remainingClass(projectedRemaining(row))">
                  {{ magnitude(projectedRemaining(row)) }}<em v-if="projectedRemaining(row) < 0" class="schedule-dialog__over">over</em>
                </span>
                <span v-else class="text-right text-xs text-gray-300" aria-hidden="true">—</span>
              </template>
            </div>

            <p v-if="factorNote" class="mb-0 px-3 pt-0.5 text-[10px] text-gray-400">{{ factorNote }}</p>
          </template>
        </div>
        <p v-if="dialog.errorFor('quantities')" id="dialog-quantities-error" class="mb-0 mt-1 text-xs text-red-600" role="alert">{{ dialog.errorFor('quantities') }}</p>

        <div v-if="!dialog.isManualMode.value && !dialog.splitOpen.value" class="mt-3 rounded-lg border border-gray-200 p-3">
          <div class="flex items-baseline justify-between gap-3">
            <span class="text-xs font-semibold text-gray-700">
              Adjustment
              <span class="text-blue-700">{{ dialog.adjustment.value > 0 ? '+' : '' }}{{ dialog.adjustment.value }}%</span>
            </span>
            <span class="text-[11px] tabular-nums text-gray-500">
              {{ dialog.calculatedTotal.value.toLocaleString() }} calculated →
              <span class="font-semibold text-gray-700">{{ dialog.totalQuantity.value.toLocaleString() }} planned</span>
            </span>
          </div>
          <v-slider
            density="compact"
            hide-details
            :min="ADJUSTMENT_MIN"
            :max="ADJUSTMENT_MAX"
            :step="ADJUSTMENT_STEP"
            :model-value="dialog.adjustment.value"
            @update:model-value="(value: number) => dialog.setAdjustment(value)"
          >
            <template #prepend><span class="text-[10px] tabular-nums text-gray-400">{{ ADJUSTMENT_MIN }}%</span></template>
            <template #append><span class="text-[10px] tabular-nums text-gray-400">+{{ ADJUSTMENT_MAX }}%</span></template>
          </v-slider>
        </div>

        <div class="mt-3">
          <label class="label" for="dialog-notes">
            Note
            <span class="font-normal text-gray-400">{{ dialog.isManualMode.value ? '(required — it names the reservation)' : '(optional)' }}</span>
          </label>
          <v-textarea
            id="dialog-notes"
            ref="notesField"
            v-model="dialog.notes.value"
            variant="outlined"
            density="compact"
            hide-details
            rows="2"
            :error="!!dialog.errorFor('notes')"
            :aria-invalid="!!dialog.errorFor('notes')"
            :aria-describedby="dialog.errorFor('notes') ? 'dialog-notes-error' : undefined"
          />
          <p v-if="dialog.errorFor('notes')" id="dialog-notes-error" class="mb-0 mt-1 text-xs text-red-600">{{ dialog.errorFor('notes') }}</p>
        </div>

        <!-- Confirming a mark replaces the footer in place rather than opening a second dialog. -->
        <div v-if="dialog.markStep.value === 'confirm' && dialog.selectedPo.value" class="mt-auto pt-4" role="group" aria-labelledby="dialog-mark-title">
          <div class="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <p id="dialog-mark-title" class="m-0 text-sm font-semibold text-gray-900">Mark {{ dialog.selectedPo.value.poNumber }} as fully scheduled?</p>
            <p class="mb-0 mt-1 text-xs text-gray-600">
              It leaves the queue and nothing new is scheduled. It comes back if the PO's lines or quantities change or the portal can count it, and the note above is saved with the mark.
            </p>
            <div class="mt-3 flex items-center justify-end gap-2">
              <button class="btn-secondary" type="button" @click="dialog.cancelMark">Back</button>
              <button class="btn-primary" type="button" @click="dialog.confirmMark">Mark as fully scheduled</button>
            </div>
          </div>
          <p v-if="dialog.markError.value" id="dialog-mark-error" class="mb-0 mt-1 text-xs text-red-600" role="alert">{{ dialog.markError.value }}</p>
        </div>
        <template v-else>
          <div class="mt-auto pt-4 flex items-center justify-end gap-2">
            <span v-if="dialog.splitOpen.value && dialog.split.footer.value.kind === 'open'" class="mr-auto text-[11px] text-amber-800">
              {{ dialog.split.footer.value.parts.join(' · ') }} of the {{ dialog.isManualMode.value ? 'reservation' : 'PO' }} stays unscheduled
            </span>
            <span v-else-if="markedLine" class="mr-auto flex min-w-0 items-center gap-2 text-[11px] text-gray-500">
              <span class="min-w-0">{{ markedLine }}</span>
              <button v-if="canWrite" class="mark-link" type="button" @click="dialog.unmarkSelected">Unmark</button>
            </span>
            <button v-else-if="showMarkLink" class="mark-link mr-auto" type="button" @click="dialog.openMarkConfirm">Mark as fully scheduled</button>
            <button class="btn-secondary" type="button" :disabled="dialog.split.saving.value" @click="dialog.close">Cancel</button>
            <button
              class="btn-primary"
              type="button"
              :disabled="dialog.splitOpen.value ? dialog.split.locked.value : dialog.submitting.value"
              @click="dialog.requestSubmit"
            >
              {{ submitLabel }}
            </button>
          </div>
          <p v-if="dialog.markError.value" id="dialog-mark-error" class="mb-0 mt-1 text-xs text-red-600" role="alert">{{ dialog.markError.value }}</p>
        </template>
        <p v-if="dialog.splitOpen.value && dialog.errorFor('split')" id="dialog-split-error" class="mb-0 mt-1 text-right text-xs text-red-600" role="alert">
          {{ dialog.errorFor('split') }}
        </p>
      </div>

      <PoLineItemsPanel v-if="showLineItems" :po="dialog.selectedPo.value!" class="min-w-0 flex-1 border-l border-gray-200" />
      <SplitAcrossWeeksPanel
        v-else-if="dialog.splitOpen.value"
        :split="dialog.split"
        :title="dialog.isManualMode.value ? 'Reservation' : (dialog.poNumber.value ?? '')"
        @show-on-board="showOnBoard"
      />
    </div>
  </v-dialog>

  <ConfirmDialog
    :open="dialog.pendingOverbook.value"
    title="Overbook capacity?"
    :message="overbookMessage"
    confirm-label="Schedule anyway"
    :secondary-label="!dialog.splitOpen.value && dialog.canOpenSplit.value ? 'Split across weeks' : undefined"
    @confirm="dialog.submitConfirmed"
    @cancel="dialog.cancelOverbook"
    @secondary="onSplitFromOverbook"
  />
</template>

<script setup lang="ts">
  import { computed, nextTick, ref, watch } from 'vue';
  import type { VSelect, VTextarea } from 'vuetify/components';

  import ConfirmDialog from '@/components/common/ConfirmDialog.vue';
  import PoLineItemsPanel from '@/components/scheduling/PoLineItemsPanel.vue';
  import SplitAcrossWeeksPanel from '@/components/scheduling/SplitAcrossWeeksPanel.vue';
  import { useAccess } from '@/composables/useAccess';
  import { ADJUSTMENT_MAX, ADJUSTMENT_MIN, ADJUSTMENT_STEP, DIALOG_FIELD, useScheduleDialog } from '@/composables/useScheduleDialog';
  import type { DialogTypeRow } from '@/composables/useScheduleDialog';
  import { useCapacityCalendarStore } from '@/store/capacityCalendarStore';
  import { usePurchaseOrderStore } from '@/store/purchaseOrderStore';
  import { PERMISSIONS } from '@/types/access';
  import type { PurchaseOrder } from '@/types/purchaseOrders';
  import { pluralWeeks } from '@/utils/splitAcrossWeeks';
  import { markedOnLabel } from '@/utils/toScheduleQueue';

  const emit = defineEmits<{ scheduled: [] }>();

  const dialog = useScheduleDialog(() => emit('scheduled'));
  const poStore = usePurchaseOrderStore();
  const calendarStore = useCapacityCalendarStore();
  const poLoading = computed(() => poStore.loading);
  const { hasPermission } = useAccess();
  // Marking is part of scheduling, so it is gated by the same permission as the board's Schedule.
  const canWrite = computed(() => hasPermission(PERMISSIONS.WRITE_PO));

  const vendorField = ref<InstanceType<typeof VSelect> | null>(null);
  const weekField = ref<InstanceType<typeof VSelect> | null>(null);
  const notesField = ref<InstanceType<typeof VTextarea> | null>(null);
  const quantitiesGroup = ref<HTMLDivElement | null>(null);

  // A failed submit attempt reveals the errors and moves focus to the first one, same as a
  // native form's validation UX. Keyed on the attempt counter, never on the invalid field
  // itself: fixing one field re-picks the next invalid one, and focusing it mid-typing
  // would pull the caret out of the field being corrected.
  watch(
    () => dialog.submitAttempts.value,
    async () => {
      const field = dialog.firstInvalidField.value;
      if (!field) return;
      await nextTick();
      if (field === DIALOG_FIELD.VENDOR) vendorField.value?.focus();
      else if (field === DIALOG_FIELD.WEEK) weekField.value?.focus();
      else if (field === DIALOG_FIELD.NOTES) notesField.value?.focus();
      else if (field === DIALOG_FIELD.QUANTITIES) quantitiesGroup.value?.querySelector<HTMLInputElement>('input:not([disabled])')?.focus();
    },
  );
  const showMarkLink = computed(() => dialog.canMark.value && canWrite.value && !dialog.splitOpen.value && !dialog.selectedPo.value?.scheduleMark);

  const markedLine = computed(() => {
    const mark = dialog.selectedPo.value?.scheduleMark;
    if (!mark || dialog.splitOpen.value) return '';
    const date = markedOnLabel(mark.markedAt);
    return `Marked as fully scheduled by ${mark.markedBy} on ${date}`;
  });

  const FORM_WIDTH = 480;
  // The form plus a pane wide enough for a part number and its description, or for the split
  // pane's week cards; three type lines need no more width, but three capacity types on the
  // single-week form's Left/After grid do, hence the wider variant.
  const PAIR_MAX_WIDTH = 1320;
  const PAIR_MAX_WIDTH_WIDE = 1440;
  const WIDE_TYPE_COUNT = 3;

  // Only ever shown for a PO that is actually selected — clearing the PO takes the pane with
  // it rather than leaving an empty half behind.
  const showLineItems = computed(() => dialog.paneTab.value === 'lines' && dialog.selectedPo.value !== null);

  // Keyed on whether either pane renders, not on which one — this drives both the dialog's
  // fixed height and its width so switching between the two pane buttons never resizes the frame.
  const paneOpen = computed(() => showLineItems.value || dialog.splitOpen.value);

  const dialogWidth = computed(() => {
    if (!paneOpen.value) return FORM_WIDTH;
    return dialog.typeRows.value.length >= WIDE_TYPE_COUNT ? PAIR_MAX_WIDTH_WIDE : PAIR_MAX_WIDTH;
  });

  function onVendorChange(vendorId: string | null): void {
    if (vendorId) void dialog.selectVendor(vendorId);
  }

  function onWeekChange(weekKey: string | null): void {
    dialog.setWeek(weekKey);
  }

  // The figures speak for themselves under a PO number; labelling them "remaining" spent a
  // third of the line saying what the list is. Nothing to show is left blank rather than
  // captioned as unknown.
  function poItemProps(po: PurchaseOrder): Record<string, unknown> {
    const remaining = Object.entries(po.remainingTotals ?? {})
      .filter(([, value]) => value > 0)
      .map(([code, value]) => `${code} ${value.toLocaleString()}`)
      .join(' · ');
    return { subtitle: remaining || undefined };
  }

  // Calculated is derived from the selected PO's outstanding line items, so with no PO there
  // is nothing to derive — the column would be a row of zeros claiming to be a suggestion.
  const showCalculated = computed(() => !dialog.isManualMode.value);

  function plannedQuantity(row: DialogTypeRow): number {
    return dialog.quantities.value[row.capacityTypeId] ?? 0;
  }

  // What the week would be left with once this plan is saved.
  function projectedRemaining(row: DialogTypeRow): number {
    return (row.remaining ?? 0) - plannedQuantity(row);
  }

  // Answers the obvious "so where is the capacity factor?": it is applied once at import,
  // into the planning capacity that Left counts down from — it never touches the PO's own
  // quantities, so nothing in Calculated or the adjustment slider carries it.
  const factorNote = computed(() => {
    const factors = dialog.typeRows.value
      .filter((row) => row.hasPlan && row.capacityFactor !== null)
      .map((row) => `${row.typeCode} ×${row.capacityFactor}`);
    if (factors.length === 0) return '';
    return `Left counts planning capacity — this vendor's weekly capacity already scaled by ${factors.join(', ')}.`;
  });

  // Magnitude only. The column header says which figure this is and the `over` marker says
  // which side of zero it falls, so the digits never carry a minus sign for a reader to spot.
  function magnitude(value: number): string {
    return Math.abs(value).toLocaleString();
  }

  // Each figure is coloured by its own sign, never the row as a whole: red under Left means
  // the week was already overbooked before this dialog opened, red under After means saving
  // is what puts it over. One red for both made the two states indistinguishable.
  function remainingClass(value: number): string {
    return value < 0 ? 'font-semibold text-red-600' : 'text-gray-500';
  }

  const splitHint = computed(() => {
    // The submit button already narrates progress while saving (and says the save stopped
    // when it halted); this line stays quiet rather than repeating it, or falling through to
    // the stale "complete" wording it would otherwise read as mid-save.
    if (dialog.split.locked.value) return '';
    const footer = dialog.split.footer.value;
    if (footer.kind === 'over') return `${pluralWeeks(footer.weeks.length)} over capacity`;
    if (footer.kind === 'open') return `${footer.parts.join(' · ')} not placed`;
    return `split into ${pluralWeeks(dialog.split.evaluation.value.plannedWeekKeys.length)}`;
  });
  const splitHintClass = computed(() => {
    const kind = dialog.split.footer.value.kind;
    return kind === 'over' ? 'font-semibold text-red-600' : kind === 'open' ? 'font-semibold text-amber-800' : 'text-gray-500';
  });

  const submitLabel = computed(() => {
    // "Reserve" rather than "Block": the row this writes is a reservation everywhere else in
    // the interface, and the button that makes one should say the same word.
    const verb = dialog.isManualMode.value ? 'Reserve' : 'Schedule';
    if (dialog.splitOpen.value) {
      const footer = dialog.split.footer.value;
      // footer.index counts weeks already saved (0-based), so the week in flight is index + 1;
      // capped at total in case the last save's "saved" flag lands before `saving` clears.
      if (footer.kind === 'saving') return `Saving ${Math.min(footer.index + 1, footer.total)} of ${footer.total}…`;
      if (footer.kind === 'halted') return 'Saving stopped';
      const ev = dialog.split.evaluation.value;
      return `${verb} ${dialog.split.placedTotal.value.toLocaleString()} · ${pluralWeeks(ev.plannedWeekKeys.length)}`;
    }
    const quantity = dialog.totalQuantity.value > 0 ? dialog.totalQuantity.value.toLocaleString() : '';
    return quantity.length > 0 ? `${verb} ${quantity}` : verb;
  });

  const overbookMessage = computed(() => {
    if (dialog.splitOpen.value) {
      return dialog.split.footer.value.kind === 'over'
        ? dialog.split.footer.value.weeks.map((week) => `${week.label}: ${week.parts.map((part) => part.replace(/ (\S+)$/, ' over by $1')).join(', ')}`).join('\n')
        : '';
    }
    return dialog.overbookedTypes.value.map((entry) => `${entry.typeCode} over by ${entry.by.toLocaleString()}`).join('\n');
  });

  function onSplitFromOverbook(): void {
    dialog.cancelOverbook();
    dialog.openSplit();
  }

  // "Show on board": close the dialog and flash the PO's existing block in that week.
  function showOnBoard(weekKey: string): void {
    if (dialog.poNumber.value) calendarStore.setRecentAssignments([dialog.poNumber.value], [weekKey]);
    dialog.close();
  }

  defineExpose({ openDialog: dialog.openDialog });
</script>

<style scoped>
  /* Tailwind's preflight zeroes border-width globally, which flattens
     Vuetify's notched outline (it never redraws visibly even after
     restoring --v-field-border-width). Draw a plain border on the field
     wrapper instead, and hide Vuetify's own outline parts so they don't
     double-draw. Copied from PoAssignmentPicker.vue, which documents the
     same workaround; the underlying VField wrapper is shared by v-select,
     v-autocomplete, v-text-field, and v-textarea, so one selector covers
     every field in this dialog. */
  .schedule-dialog :deep(.v-field) {
    border: 1px solid rgb(203 213 225);
    border-radius: 0.375rem;
    background: white;
  }

  .schedule-dialog :deep(.v-field--focused) {
    border-color: rgb(37 99 235);
  }

  .schedule-dialog :deep(.v-field__outline) {
    display: none;
  }

  /* Vuetify gives the autocomplete's inner <input> the field's own inline padding, but
     that rule lives in @layer vuetify-components while Tailwind's preflight
     `input { padding: 0 }` is unlayered — unlayered wins, so the PO placeholder and value
     sat flush against the border while the selects beside them were indented. */
  .schedule-dialog :deep(.v-autocomplete input) {
    padding-inline: var(--v-field-padding-start) var(--v-field-padding-end);
  }

  /* One track set shared by the column headers and every quantity row, so the two line up
     without either side hard-coding widths. Sized for the 480px form: the three fixed
     columns leave the two capacity figures ~90px each, enough for "38,606 over". */
  .schedule-dialog__qty-grid,
  .schedule-dialog__qty-grid--plan-only {
    display: grid;
    grid-template-columns: 2.25rem 4.5rem 5.5rem 1fr 1fr;
    align-items: center;
    gap: 0.625rem;
  }

  /* No PO means no Calculated column; declared after the shared rule so it wins on source
     order rather than on specificity. */
  .schedule-dialog__qty-grid--plan-only {
    grid-template-columns: 2.25rem 5.5rem 1fr 1fr;
  }

  /* Reads as a state marker on the figure rather than a word in a sentence — it inherits
     the red from the figure it sits on. */
  .schedule-dialog__over {
    margin-left: 0.1875rem;
    font-size: 9px;
    font-style: normal;
    font-weight: 600;
    letter-spacing: 0.09em;
    text-transform: uppercase;
  }

  /* The quantity rows' v-text-field otherwise keeps Vuetify's default
     comfortable field height, which reads as oversized/misaligned against
     the compact gray rows around it — match .input's compact sizing instead. */
  .schedule-dialog__quantity-field :deep(.v-field__input) {
    min-height: 0;
    padding: 6px 10px;
  }

  /* Right-aligned like the Calculated cell beside it and the figures after it: the row is
     a line of numbers, and one left-aligned digit column breaks the read. */
  .schedule-dialog__quantity-field :deep(input) {
    text-align: right;
  }

  .schedule-dialog__quantity-field :deep(.v-field__append-inner) {
    padding-top: 6px;
  }

  /* Same compact sizing for the note textarea, whose default field padding
     otherwise towers over the compact fields above it. */
  .schedule-dialog :deep(.v-textarea .v-field__input) {
    min-height: 0;
    padding: 8px 10px;
  }

  .mark-link {
    padding: 4px 2px;
    font-size: 12px;
    font-weight: 600;
    color: rgb(29 78 216);
    white-space: nowrap;
  }

  .mark-link:hover {
    text-decoration: underline;
  }

  .pane-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 600;
    border-radius: 8px;
    border: 1px solid rgb(209 213 219);
    background: white;
    color: rgb(55 65 81);
  }

  .pane-btn:hover:not(:disabled) {
    background: rgb(249 250 251);
  }

  .pane-btn:disabled {
    color: rgb(156 163 175);
    cursor: not-allowed;
  }

  .pane-btn--on {
    background: rgb(239 246 255);
    color: rgb(29 78 216);
    border-color: rgb(191 219 254);
  }

  .schedule-dialog__qty-grid--totals,
  .schedule-dialog__qty-grid--totals-plan-only {
    display: grid;
    align-items: center;
    gap: 0.625rem;
  }

  .schedule-dialog__qty-grid--totals {
    grid-template-columns: 2.25rem 1fr 1fr;
  }

  /* No PO (a reservation) means no Calculated column, so the total sits alone rather than
     leaving a dead middle cell. */
  .schedule-dialog__qty-grid--totals-plan-only {
    grid-template-columns: 2.25rem 1fr;
  }
</style>
